import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { Search, UploadFile, Download } from '@mui/icons-material';

/**
 * Shared header block for the admin catalog list screens (Materiales,
 * Mano de Obra, Fletes, Listas de Precios - worker brief
 * "catalog-list-ui-pattern", 2026-09-05). Renders:
 *   - the page title + a status/count line under it
 *   - admin-only "Importar <itemNoun> CSV/XLS" and "Descargar plantilla"
 *     buttons, top-right, stacked
 *   - a search field directly below the title/count row (icon + loading
 *     spinner adornment), with room for extra page-specific controls
 *     (e.g. an items-per-page selector) in the same row via `extraControls`
 *
 * Deliberately presentational only - no page-specific business logic, no
 * knowledge of a particular catalog's shape. `ProductList.tsx` is the first
 * consumer; `InstallationList.tsx`/`FreightManagement.tsx`/
 * `PriceListManagement.tsx` are meant to adopt this unchanged.
 */
export interface CatalogListToolbarProps {
  /** Page H1 title, e.g. "Productos". */
  title: string;
  /** Rendered under the title, e.g. "Mostrando 1-50 de 200 productos". */
  subtitle?: React.ReactNode;
  /** Noun used in the import button label: "Importar {itemNoun} CSV/XLS". */
  itemNoun: string;
  /** Only admins see the Importar/Descargar plantilla buttons. */
  isAdmin: boolean;
  onImportClick: () => void;
  /**
   * Either is enough to show the "Descargar plantilla" button - omit both
   * to hide it entirely (e.g. a catalog with no importer yet).
   * `templateDownloadUrl` renders a plain `<a download>` link; prefer
   * `onDownloadTemplate` when the template needs an authenticated API call
   * (axios) rather than a public static URL.
   */
  templateDownloadUrl?: string;
  onDownloadTemplate?: () => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchLoading?: boolean;
  searchPlaceholder?: string;
  /** Extra controls rendered in the same row as the search field, e.g. a "Por página" selector. */
  extraControls?: React.ReactNode;
}

export const CatalogListToolbar: React.FC<CatalogListToolbarProps> = ({
  title,
  subtitle,
  itemNoun,
  isAdmin,
  onImportClick,
  templateDownloadUrl,
  onDownloadTemplate,
  searchValue,
  onSearchChange,
  searchLoading,
  searchPlaceholder,
  extraControls,
}) => {
  const showTemplateButton = !!templateDownloadUrl || !!onDownloadTemplate;

  return (
    <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 4px 24px 0 rgba(60,72,100,0.10)' }}>
      <CardContent>
        <Box
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          gap={3}
        >
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          {isAdmin && (
            <Box display="flex" flexDirection="column" gap={1} alignItems="stretch">
              <Button
                variant="outlined"
                color="primary"
                startIcon={<UploadFile />}
                onClick={onImportClick}
                sx={{ boxShadow: '0 2px 8px 0 rgba(66,133,244,0.08)', borderRadius: 2 }}
              >
                Importar {itemNoun} CSV/XLS
              </Button>
              {showTemplateButton && (
                <Button
                  variant="text"
                  color="primary"
                  size="small"
                  startIcon={<Download />}
                  {...(templateDownloadUrl
                    ? { component: 'a', href: templateDownloadUrl, download: true }
                    : {})}
                  onClick={onDownloadTemplate}
                  sx={{ borderRadius: 2 }}
                >
                  Descargar plantilla
                </Button>
              )}
            </Box>
          )}
        </Box>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" mt={3}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder={searchPlaceholder || 'Buscar...'}
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {searchLoading ? <CircularProgress size={20} /> : <Search />}
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: '220px', background: '#f8fafc', borderRadius: 2 }}
          />
          {extraControls}
        </Box>
      </CardContent>
    </Card>
  );
};

export default CatalogListToolbar;
